import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import { Field, FieldProps, Form, Formik } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import { AlertContent, Button, CentBasedInputCurrency, ErrorText, H4, Label, ModalFactory, ModalFooter, ModalHeader, ModalTitle, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { ICheck, IPOSTicketTotals } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { getDateTimeWithoutZone } from 'utils/date';

const MAX_TIP_CASH = 500000;

interface IEditCheckAmountsProps {
  check: ICheck;
  timeZone: string;
  onSave(check: ICheck): void;
  onClose(): void;
}

const Layout = styled.section<{ $hidden?: boolean }>`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(640)};
  height: auto;
  margin: ${toRem(theme.space['3xl'])} 0;
  padding: ${toRem(theme.space['3xl'])};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;

      &:first-of-type + div {
        display: none;
      }
    `}
`;

const ModalSubTitle = styled(ModalTitle)`
  margin-top: ${toRem(theme.space.lg)};
  font-size: ${toRem(theme.font.size.lg)};
  line-height: ${lineHeight(theme.font.size.lg)};

  &.date {
    margin-top: ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[600]};
    font-weight: ${theme.font.weight.regular};
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(theme.space.sm)};
  width: 100%;
  height: auto;
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  justify-items: center;
  border: 1px solid ${theme.color.neutral[200]};

  > * {
    margin-bottom: 0 !important;
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${toRem(theme.space.lg)};
  height: auto;
  width: 100%;
  border-radius: ${toRem(11)} ${toRem(11)} 0 0;
  background-color: ${theme.color.neutral[100]};
  padding: ${toRem(theme.space.lg)};
  border-bottom: 1px solid ${theme.color.neutral[200]};

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: ${toRem(theme.space.sm)};
  }
`;

const TopRowItem = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.medium};
    text-align: center;
    word-break: break-word;

    &.check-time {
      font-size: ${toRem(theme.font.size.xxs)};
      line-height: ${lineHeight(theme.font.size.xxs)};
      font-weight: ${theme.font.weight.regular};
      color: ${theme.color.neutral[600]};
    }
  }

  & > label {
    // required to override existing specificity
    margin-bottom: ${toRem(theme.space.sm)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};

    &.display-id {
      position: relative;
      top: ${toRem(4)};
      font-size: ${toRem(theme.font.size.lg)};
      line-height: ${lineHeight(theme.font.size.lg)};
      font-weight: ${theme.font.weight.bold};
      color: ${theme.color.neutral[900]};
    }
  }

  & > h4 {
    // required to override existing specificity
    margin-bottom: ${toRem(theme.space.md)};
    font-weight: ${theme.font.weight.bold};
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const FormWrapper = styled.div`
  align-items: start;
  background-color: ${theme.color.neutral[50]};
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.lg)};
  width: 100%;
  border-radius: 0 0 ${toRem(11)} ${toRem(11)};

  label {
    color: ${theme.color.neutral[500]};
  }
`;

const FormSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(theme.space.sm)} 0;
  gap: ${toRem(theme.space.sm)};
  width: 100%;

  h4 {
    font-size: ${toRem(theme.font.size.md)};
    line-height: ${lineHeight(theme.font.size.md)};
    font-weight: ${theme.font.weight.semiBold};
    margin-bottom: ${toRem(theme.space.sm)};
  }
`;

const FormInputs = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
`;

const InputContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.sm)};

  label {
    margin-bottom: ${toRem(theme.space.sm)};
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }

  .error {
    input {
      border: 1px solid ${theme.color.danger[50]};
    }
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  padding: ${toRem(theme.space.sm)} 0;

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
  }
`;

export const EditCheckAmounts: React.FC<IEditCheckAmountsProps> = ({ check, timeZone, onClose, onSave }) => {
  const [isDirtyCancel, setIsDirtyCancel] = useState(false);

  const setInitialValues = (): IPOSTicketTotals => ({ ...check.totals });

  const validationSchema = Yup.object().shape({
    gross: Yup.number().required(),
    net: Yup.number().required(),
    tip: Yup.object().shape({
      gross: Yup.number().required(),
      net: Yup.number().required(),
      cash: Yup.number()
        .required()
        .typeError('Invalid value')
        .test('maxTipCashValue', `Maximum is ${centsToDollarsDisplay(MAX_TIP_CASH)}`, (value) => !(value > MAX_TIP_CASH)),
      creditCard: Yup.number().required(),
      creditCardFee: Yup.number().required(),
    }),
    autoGratuity: Yup.object().shape({
      gross: Yup.number().required(),
      net: Yup.number().required(),
      cash: Yup.number().required(),
      creditCard: Yup.number().required(),
      creditCardFee: Yup.number().required(),
    }),
  });

  const handleSubmit = (values: any) => {
    // Recalculate tip totals
    const tipTotals = {
      ...values.tip,
      gross: values.tip.creditCard + values.tip.cash,
      net: values.tip.creditCard + values.tip.cash - values.tip.creditCardFee,
    };

    // Recalculate check totals
    const totals = {
      ...values,
      gross: tipTotals.gross + values.autoGratuity.gross,
      net: tipTotals.net + values.autoGratuity.net,
      tip: tipTotals,
    };

    onSave({ ...check, totals });
  };

  const handleCancel = (dirty: boolean) => {
    setIsDirtyCancel(dirty);
    if (dirty) {
      showCancelModal();
    } else {
      onClose();
    }
  };

  const [showCancelModal, hideCancelModal] = useModal(
    () => (
      <ModalFactory
        customModal
        onClose={() => {
          setIsDirtyCancel(false);
          hideCancelModal();
        }}
        isActive
        closeOnOutSideClick={false}
        hideCloseButton
      >
        <AlertContent
          title="Cancel Changes?"
          message="Do you want to cancel the changes you’ve made to this check?"
          leftActionLabel="Go Back"
          rightActionLabel="Cancel Changes"
          onLeftAction={() => {
            setIsDirtyCancel(false);
            hideCancelModal();
          }}
          onRightAction={() => {
            hideCancelModal();
            onClose();
          }}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    ),
    [onClose]
  );

  return (
    <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false} hideCloseButton>
      <Layout $hidden={isDirtyCancel} data-cy="edit-check-amounts-modal">
        <Formik initialValues={setInitialValues()} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ dirty, isValid, values, setFieldValue }) => (
            <Form>
              <ModalHeader data-cy="split-check-modal-header">
                <ModalTitle>Edit Amounts</ModalTitle>
                <ModalSubTitle>Check #{check.displayId}</ModalSubTitle>
                <ModalSubTitle className="date">{getDateTimeWithoutZone(check.processingTime, timeZone)}</ModalSubTitle>
              </ModalHeader>
              <ModalBody>
                <TopRow data-cy="split-check-modal-check-info">
                  <TopRowItem data-cy="day-segment">
                    <Label>Day Segment</Label>
                    <Text>{check.segmentName}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="employee">
                    <Label>Employee</Label>
                    <Text>{check.employeeName}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="tip">
                    <Label>Tips & Autograts</Label>
                    <Text>{centsToDollarsDisplay(check.totals.gross)}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="total">
                    <Label>Total</Label>
                    <Text>{centsToDollarsDisplay(check.gross)}</Text>
                  </TopRowItem>
                </TopRow>
                <FormWrapper data-cy="edit-check-amounts-modal-form">
                  <FormSection>
                    <H4>Tips</H4>
                    <FormInputs>
                      <InputContainer>
                        <Label>Credit Card</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-tip-cc" value={check.totals.tip.creditCard?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                      <InputContainer>
                        <Field name="tip.cash">
                          {({ field, meta }: FieldProps) => (
                            <>
                              <Label>Cash</Label>
                              <CentBasedInputCurrency
                                {...field}
                                data-cy="edit-check-amounts-modal-form-input-tip-cash"
                                value={values.tip.cash?.toString()}
                                onChange={(value: string) => setFieldValue('tip.cash', parseInt(value, 10))}
                                allowZero
                                className={meta.touched && meta.error ? 'error' : ''}
                              />
                              {meta.touched && meta.error && (
                                <ErrorText size="xxsmall" data-cy="edit-check-amounts-modal-form-input-tip-cash-error">
                                  {meta.error}
                                </ErrorText>
                              )}
                            </>
                          )}
                        </Field>
                      </InputContainer>
                    </FormInputs>
                    <TotalWrapper>
                      <Label>Total:</Label>
                      <Text size="small">{centsToDollarsDisplay(values.tip.creditCard + values.tip.cash)}</Text>
                    </TotalWrapper>
                  </FormSection>
                  <FormSection>
                    <H4>Autograts</H4>
                    <FormInputs>
                      <InputContainer>
                        <Label>Credit Card</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-auto-gratuity-cc" value={check.totals.autoGratuity.creditCard?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                      <InputContainer>
                        <Label>Cash</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-auto-gratuity-cash" value={check.totals.autoGratuity.cash?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                    </FormInputs>
                    <TotalWrapper>
                      <Label>Total:</Label>
                      <Text size="small">{centsToDollarsDisplay(values.autoGratuity.creditCard + values.autoGratuity.cash)}</Text>
                    </TotalWrapper>
                  </FormSection>
                </FormWrapper>
              </ModalBody>
              <ModalFooter columns={2} data-cy="split-check-modal-footer">
                <StyledButton data-cy="cancel-btn" label="Cancel" appearance="white" size="large" onClick={() => handleCancel(dirty)} />
                <StyledButton data-cy="save-btn" label="Save" disabled={!(isValid && dirty)} isProcessing={false} type="submit" appearance="primary" size="large" />
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Layout>
    </ModalFactory>
  );
};
